import {
  BadgeCheck,
  Check,
  ChevronDown,
  ChevronRight,
  CircleDollarSign,
  Clock3,
  Copy,
  Download,
  FileText,
  Folder,
  FolderOpen,
  Images,
  Layers3,
  LoaderCircle,
  MapPin,
  PackageCheck,
  RefreshCw,
  Search,
  Tags,
  Truck,
  X,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import { api, downloadFile } from "../api";
import { EmptyState, LoadingState, formatDate } from "../components";
import type { CategorySiteSummary, CategorySyncRun, MeliCategory, MeliCategoryAttribute, MeliPublishSchema, RelatedMeliCategory } from "../types";

type CategoryTab = "info" | "publish" | "attributes" | "sites" | "path";

const ROOT_KEY = "__ROOT__";
const ALL_SITES_EXPORT_URL = "/downloads/MeliForge-all-sites-category-database-current.zip";
const siteFallbacks = [
  { siteCode: "CBT", label: "跨境站" },
  { siteCode: "MLM", label: "墨西哥" },
  { siteCode: "MLB", label: "巴西" },
  { siteCode: "MCO", label: "哥伦比亚" },
  { siteCode: "MLC", label: "智利" },
  { siteCode: "MLA", label: "阿根廷" },
  { siteCode: "MLU", label: "乌拉圭" },
];

function treeKey(siteCode: string, parentId?: string | null) {
  return `${siteCode}:${parentId ?? ROOT_KEY}`;
}

function valuePreview(values: unknown[]) {
  const names = values.flatMap((value) => {
    if (typeof value === "string" || typeof value === "number") return [String(value)];
    if (value && typeof value === "object" && "name" in value) return [String(value.name)];
    return [];
  });
  return names.slice(0, 4).join("、");
}

function runLabel(run?: CategorySyncRun) {
  if (!run) return "尚未同步";
  if (run.status === "RUNNING") return "同步中";
  if (run.status === "SUCCESS") return "同步完成";
  if (run.status === "PARTIAL") return "部分完成";
  return "同步失败";
}

function YesNo({ value }: { value: boolean }) {
  return <span className={value ? "catalog-yes" : "catalog-no"}>{value ? <Check size={13} /> : <X size={13} />}{value ? "是" : "否"}</span>;
}

function requirementLabel(requirement: string) {
  if (requirement === "REQUIRED") return "必填";
  if (requirement === "CONDITIONAL") return "条件必填";
  if (requirement === "SYSTEM") return "系统生成";
  return "选填";
}

function scopeLabel(scope: string) {
  if (scope === "VARIATION") return "规格级";
  if (scope === "SYSTEM") return "系统级";
  return "商品级";
}

function constraintHint(constraints: Record<string, unknown>) {
  const hints: string[] = [];
  if (Number(constraints.maxLength) > 0) hints.push(`最多 ${Number(constraints.maxLength)} 字符`);
  if (Number(constraints.maximumItems) > 0) hints.push(`最多 ${Number(constraints.maximumItems)} 项`);
  if (Number(constraints.minimumItems) > 0) hints.push(`至少 ${Number(constraints.minimumItems)} 项`);
  if (constraints.minimum !== undefined && constraints.minimum !== null) hints.push(`最低 ${String(constraints.minimum)}${constraints.currencyId ? ` ${String(constraints.currencyId)}` : ""}`);
  return hints.join(" · ");
}

interface TreeBranchProps {
  nodes: MeliCategory[];
  depth: number;
  selectedId?: string;
  expanded: Set<string>;
  loadingKeys: Set<string>;
  childrenByParent: Record<string, MeliCategory[]>;
  siteCode: string;
  onToggle: (category: MeliCategory) => void;
  onSelect: (category: MeliCategory) => void;
}

function CategoryTreeBranch(props: TreeBranchProps) {
  return <ul className="catalog-tree-branch">
    {props.nodes.map((category) => {
      const key = treeKey(props.siteCode, category.categoryId);
      const isExpanded = props.expanded.has(category.categoryId);
      const children = props.childrenByParent[key] ?? [];
      const style = { "--tree-depth": props.depth } as CSSProperties;
      return <li key={category.categoryId}>
        <div className={`catalog-tree-row ${props.selectedId === category.categoryId ? "selected" : ""}`} style={style}>
          {category.childCount ? <button className="catalog-tree-toggle" aria-label={`${isExpanded ? "收起" : "展开"}${category.name}`} title={isExpanded ? "收起" : "展开"} onClick={() => props.onToggle(category)}>{isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</button> : <span className="catalog-tree-spacer" />}
          <button className="catalog-tree-label" onClick={() => props.onSelect(category)}>
            {category.childCount ? isExpanded ? <FolderOpen size={14} /> : <Folder size={14} /> : <span className="catalog-leaf-dot" />}
            <span><strong>{category.name}</strong><small>{category.categoryId}</small></span>
          </button>
          <span className="catalog-tree-count" title="该类目商品数量">{category.totalItems.toLocaleString()}</span>
        </div>
        {isExpanded ? props.loadingKeys.has(key) ? <div className="catalog-tree-loading" style={style}><LoaderCircle size={14} className="spin" />读取中</div> : children.length ? <CategoryTreeBranch {...props} nodes={children} depth={props.depth + 1} /> : <div className="catalog-tree-empty" style={style}>没有下级类目</div> : null}
      </li>;
    })}
  </ul>;
}

export function Categories() {
  const [siteCode, setSiteCode] = useState("MLM");
  const [sites, setSites] = useState<CategorySiteSummary[]>([]);
  const [runs, setRuns] = useState<CategorySyncRun[]>([]);
  const [childrenByParent, setChildrenByParent] = useState<Record<string, MeliCategory[]>>({});
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [loadingKeys, setLoadingKeys] = useState<Set<string>>(() => new Set());
  const [selected, setSelected] = useState<MeliCategory | null>(null);
  const [attributes, setAttributes] = useState<MeliCategoryAttribute[]>([]);
  const [relatedSites, setRelatedSites] = useState<RelatedMeliCategory[]>([]);
  const [publishSchema, setPublishSchema] = useState<MeliPublishSchema | null>(null);
  const [activeTab, setActiveTab] = useState<CategoryTab>("info");
  const [detailLoading, setDetailLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [searchedQuery, setSearchedQuery] = useState("");
  const [searchResults, setSearchResults] = useState<MeliCategory[]>([]);
  const [searching, setSearching] = useState(false);
  const [message, setMessage] = useState("");
  const selectionRequest = useRef(0);
  const siteRequest = useRef(0);

  const selectCategory = useCallback(async (category: MeliCategory) => {
    const requestId = selectionRequest.current + 1;
    selectionRequest.current = requestId;
    setSelected(category);
    setActiveTab("info");
    setDetailLoading(true);
    setAttributes([]);
    setRelatedSites([]);
    setPublishSchema(null);
    try {
      const [detail, related, schema] = await Promise.all([
        api.categoryAttributes(category.siteCode, category.categoryId),
        api.categoryRelatedSites(category.siteCode, category.categoryId),
        api.categoryPublishSchema(category.siteCode, category.categoryId),
      ]);
      if (selectionRequest.current !== requestId) return;
      setSelected(detail.category);
      setAttributes(detail.items);
      setRelatedSites(related.items);
      setPublishSchema(schema);
    } catch (error) {
      if (selectionRequest.current === requestId) setMessage(error instanceof Error ? error.message : "类目详情加载失败。");
    } finally {
      if (selectionRequest.current === requestId) setDetailLoading(false);
    }
  }, []);

  const loadSummary = useCallback(async () => {
    try {
      const [siteResult, runResult] = await Promise.all([api.categorySites(), api.categorySyncRuns(siteCode)]);
      setSites(siteResult.items);
      setRuns(runResult.items);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "类目资料加载失败。");
    }
  }, [siteCode]);

  useEffect(() => { void loadSummary(); }, [loadSummary]);

  const latestRun = runs[0];
  const running = latestRun?.status === "RUNNING";
  useEffect(() => {
    if (!running) return;
    const timer = window.setInterval(() => void loadSummary(), 3000);
    return () => window.clearInterval(timer);
  }, [loadSummary, running]);

  useEffect(() => {
    const requestId = siteRequest.current + 1;
    siteRequest.current = requestId;
    selectionRequest.current += 1;
    setSelected(null);
    setAttributes([]);
    setRelatedSites([]);
    setPublishSchema(null);
    setSearch("");
    setSearchedQuery("");
    setSearchResults([]);
    setExpanded(new Set());
    const rootKey = treeKey(siteCode);
    setLoadingKeys(new Set([rootKey]));

    void (async () => {
      try {
        const rootResult = await api.categoryTree(siteCode);
        if (siteRequest.current !== requestId) return;
        setChildrenByParent((current) => ({ ...current, [rootKey]: rootResult.items }));
        const firstRoot = rootResult.items[0];
        if (!firstRoot) return;
        let initialCategory = firstRoot;
        if (firstRoot.childCount) {
          const firstChildren = await api.categoryTree(siteCode, firstRoot.categoryId);
          if (siteRequest.current !== requestId) return;
          setChildrenByParent((current) => ({ ...current, [treeKey(siteCode, firstRoot.categoryId)]: firstChildren.items }));
          setExpanded(new Set([firstRoot.categoryId]));
          const firstChild = firstChildren.items[0] ?? firstRoot;
          initialCategory = firstChild;
          if (firstChild.childCount) {
            const grandchildren = await api.categoryTree(siteCode, firstChild.categoryId);
            if (siteRequest.current !== requestId) return;
            setChildrenByParent((current) => ({ ...current, [treeKey(siteCode, firstChild.categoryId)]: grandchildren.items }));
            setExpanded(new Set([firstRoot.categoryId, firstChild.categoryId]));
            initialCategory = grandchildren.items.find((item) => item.listingAllowed) ?? grandchildren.items[0] ?? firstChild;
          }
        }
        if (siteRequest.current === requestId) void selectCategory(initialCategory);
      } catch (error) {
        if (siteRequest.current === requestId) setMessage(error instanceof Error ? error.message : "类目树加载失败。");
      } finally {
        if (siteRequest.current === requestId) setLoadingKeys((current) => { const next = new Set(current); next.delete(rootKey); return next; });
      }
    })();
  }, [selectCategory, siteCode]);

  const toggleCategory = useCallback((category: MeliCategory) => {
    const childKey = treeKey(category.siteCode, category.categoryId);
    if (expanded.has(category.categoryId)) {
      setExpanded((current) => { const next = new Set(current); next.delete(category.categoryId); return next; });
      return;
    }
    setExpanded((current) => new Set(current).add(category.categoryId));
    if (childrenByParent[childKey]) return;
    setLoadingKeys((current) => new Set(current).add(childKey));
    void api.categoryTree(category.siteCode, category.categoryId).then((result) => {
      setChildrenByParent((current) => ({ ...current, [childKey]: result.items }));
    }).catch((error) => {
      setMessage(error instanceof Error ? error.message : "下级类目加载失败。");
    }).finally(() => {
      setLoadingKeys((current) => { const next = new Set(current); next.delete(childKey); return next; });
    });
  }, [childrenByParent, expanded]);

  async function runSearch() {
    const query = search.trim();
    if (!query) {
      setSearchResults([]);
      setSearchedQuery("");
      return;
    }
    setSearching(true);
    try {
      const params = new URLSearchParams({ siteCode, search: query, page: "1", pageSize: "50" });
      const result = await api.categories(params);
      setSearchResults(result.items);
      setSearchedQuery(query);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "类目搜索失败。");
    } finally {
      setSearching(false);
    }
  }

  async function sync() {
    setMessage("");
    try {
      const run = await api.syncCategories(siteCode);
      setRuns((current) => [run, ...current]);
      setMessage(`${siteCode} 类目同步已启动。`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "类目同步启动失败。");
    }
  }

  async function copyCategoryId() {
    if (!selected) return;
    try {
      await navigator.clipboard.writeText(selected.categoryId);
      setMessage(`已复制类目 ID：${selected.categoryId}`);
    } catch {
      setMessage("浏览器未允许复制，请手动选择类目 ID。");
    }
  }

  async function exportAttributes() {
    if (!selected) return;
    setMessage("");
    try {
      await downloadFile(
        `/api/categories/${encodeURIComponent(selected.siteCode)}/${encodeURIComponent(selected.categoryId)}/attributes/export`,
        `meliforge-${selected.siteCode}-${selected.categoryId}-attributes.xlsx`,
      );
      setMessage(`已导出 ${selected.categoryId} 的 ${attributes.length} 条属性。`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "类目属性导出失败。");
    }
  }

  const selectedSite = sites.find((site) => site.siteCode === siteCode);
  const siteOptions = sites.length ? sites : siteFallbacks.map((site) => ({ ...site, categoryCount: 0, listingAllowedCount: 0, attributeCategoryCount: 0, attributeCount: 0, maxLevel: 0, syncedAt: null, attributesSyncedAt: null }));
  const rootNodes = childrenByParent[treeKey(siteCode)] ?? [];
  const progress = latestRun?.attributeCategoryTotal ? Math.round((latestRun.attributeCategoryCount / latestRun.attributeCategoryTotal) * 100) : 0;
  const parent = selected?.path.at(-2);

  return <div className="catalog-page">
    <header className="catalog-page-header">
      <div><span className="page-eyebrow">CATALOG DATABASE</span><h1>类目中心</h1><p>Mercado Libre 官方类目数据库</p></div>
      <div className="catalog-header-actions">
        <span>数据同步：{selectedSite?.syncedAt ? formatDate(selectedSite.syncedAt) : "--"}</span>
        <a className="button secondary" href={ALL_SITES_EXPORT_URL} download="MeliForge-all-sites-category-database-current.zip"><Download size={17} />导出全部站点</a>
        <button className="button primary" disabled={running} onClick={() => void sync()}>{running ? <LoaderCircle size={17} className="spin" /> : <RefreshCw size={17} />}{running ? `同步中 ${progress}%` : "同步当前站点"}</button>
      </div>
    </header>

    {message ? <div className="inline-message catalog-message" role="status"><span>{message}</span><button className="icon-button compact" aria-label="关闭提示" title="关闭" onClick={() => setMessage("")}><X size={15} /></button></div> : null}
    {latestRun && latestRun.status !== "SUCCESS" ? <div className={`category-sync-strip ${latestRun.status.toLowerCase()}`}><div className="category-sync-title"><span>{running ? <LoaderCircle size={16} className="spin" /> : latestRun.status === "FAILED" ? <X size={16} /> : <Check size={16} />}</span><strong>{runLabel(latestRun)}</strong><small>{latestRun.attributeCategoryCount.toLocaleString()} / {latestRun.attributeCategoryTotal.toLocaleString()} 个可发布类目</small></div>{running ? <div className="category-progress"><span style={{ width: `${progress}%` }} /></div> : null}</div> : null}

    <section className="catalog-metrics" aria-label="类目统计">
      <div className="catalog-metric blue"><div><span>全部类目</span><strong>{(selectedSite?.categoryCount ?? 0).toLocaleString()}</strong><small>官方类目总数</small></div><FileText size={30} /></div>
      <div className="catalog-metric green"><div><span>可发布类目</span><strong>{(selectedSite?.listingAllowedCount ?? 0).toLocaleString()}</strong><small>可用于发布商品</small></div><PackageCheck size={30} /></div>
      <div className="catalog-metric violet"><div><span>已启用属性</span><strong>{(selectedSite?.attributeCount ?? 0).toLocaleString()}</strong><small>类目属性总数</small></div><Tags size={30} /></div>
      <div className="catalog-metric amber"><div><span>最近同步</span><strong>{selectedSite?.syncedAt ? formatDate(selectedSite.syncedAt) : "--"}</strong><small>数据最后更新时间</small></div><Clock3 size={30} /></div>
    </section>

    <div className="catalog-browser">
      <aside className="catalog-tree-panel">
        <div className="catalog-panel-title"><div><Layers3 size={17} /><strong>类目树</strong></div><select aria-label="类目站点" value={siteCode} onChange={(event) => setSiteCode(event.target.value)}>{siteOptions.map((site) => <option key={site.siteCode} value={site.siteCode}>{site.siteCode} · {site.label}</option>)}</select></div>
        <form className="catalog-tree-search" onSubmit={(event) => { event.preventDefault(); void runSearch(); }}><Search size={15} /><input aria-label="搜索类目树" value={search} onChange={(event) => { setSearch(event.target.value); if (!event.target.value) { setSearchResults([]); setSearchedQuery(""); } }} placeholder="搜索类目名称或 ID" /><button aria-label="搜索" title="搜索" disabled={searching}>{searching ? <LoaderCircle size={15} className="spin" /> : <ChevronRight size={15} />}</button></form>
        <div className="catalog-tree-scroll">
          {searchedQuery && searchResults.length ? <div className="catalog-search-results"><span>{searchResults.length} 条匹配</span>{searchResults.map((category) => <button key={category.categoryId} className={selected?.categoryId === category.categoryId ? "selected" : ""} onClick={() => void selectCategory(category)}><strong>{category.name}</strong><small>{category.categoryId} · {category.pathText}</small></button>)}</div> : searchedQuery && !searching && searchResults.length === 0 ? <EmptyState title="没有匹配的类目" /> : loadingKeys.has(treeKey(siteCode)) ? <LoadingState /> : <CategoryTreeBranch nodes={rootNodes} depth={0} selectedId={selected?.categoryId} expanded={expanded} loadingKeys={loadingKeys} childrenByParent={childrenByParent} siteCode={siteCode} onToggle={toggleCategory} onSelect={(category) => void selectCategory(category)} />}
        </div>
      </aside>

      <main className="catalog-detail-panel">
        {selected ? <>
          <div className="catalog-detail-head">
            <div><div className="catalog-title-line"><h2>{selected.name}</h2><code>{selected.categoryId}</code>{selected.listingAllowed ? <span className="catalog-listing-badge"><BadgeCheck size={14} />可发布类目</span> : null}</div><div className="catalog-breadcrumb"><MapPin size={13} />{selected.path.map((part, index) => <span key={part.id}>{index ? <ChevronRight size={12} /> : null}{part.name}</span>)}</div></div>
            <div className="catalog-detail-actions">
              <button className="button secondary compact" disabled={detailLoading} onClick={() => void exportAttributes()}><Download size={16} />导出属性</button>
              <button className="icon-button" aria-label="复制类目 ID" title="复制类目 ID" onClick={() => void copyCategoryId()}><Copy size={17} /></button>
            </div>
          </div>

          <div className="catalog-tabs" role="tablist" aria-label="类目详情视图">
            <button role="tab" aria-selected={activeTab === "info"} className={activeTab === "info" ? "active" : ""} onClick={() => setActiveTab("info")}>类目信息</button>
            <button role="tab" aria-selected={activeTab === "publish"} className={activeTab === "publish" ? "active" : ""} onClick={() => setActiveTab("publish")}>发布字段 <span>{publishSchema?.summary.totalFields ?? 0}</span></button>
            <button role="tab" aria-selected={activeTab === "attributes"} className={activeTab === "attributes" ? "active" : ""} onClick={() => setActiveTab("attributes")}>属性列表 <span>{attributes.length}</span></button>
            <button role="tab" aria-selected={activeTab === "sites"} className={activeTab === "sites" ? "active" : ""} onClick={() => setActiveTab("sites")}>关联站点 <span>{relatedSites.length}</span></button>
            <button role="tab" aria-selected={activeTab === "path"} className={activeTab === "path" ? "active" : ""} onClick={() => setActiveTab("path")}>类目树路径</button>
          </div>

          {detailLoading ? <LoadingState /> : activeTab === "info" ? <div className="catalog-info-view">
            <section className="catalog-info-section"><h3>基本信息</h3><dl className="catalog-facts"><div><dt>类目 ID</dt><dd className="mono">{selected.categoryId}</dd></div><div><dt>官方名称</dt><dd>{selected.name}</dd></div><div><dt>层级</dt><dd>第 {selected.level} 级</dd></div><div><dt>父类目</dt><dd>{parent ? `${parent.id} · ${parent.name}` : "顶级类目"}</dd></div><div><dt>可发布商品</dt><dd><YesNo value={selected.listingAllowed} /></dd></div><div><dt>要求品牌</dt><dd><YesNo value={selected.brandRequired} /></dd></div><div><dt>商品数量</dt><dd>{selected.totalItems.toLocaleString()}</dd></div><div><dt>目录域</dt><dd className="mono catalog-domain">{selected.catalogDomain ?? "--"}</dd></div></dl></section>
            <aside className="catalog-quick-section"><h3>快速操作</h3><div><button onClick={() => setActiveTab("attributes")}><Tags size={19} /><span>查看属性</span></button><button onClick={() => setActiveTab("sites")}><Layers3 size={19} /><span>关联站点</span></button><button onClick={() => setActiveTab("path")}><MapPin size={19} /><span>查看路径</span></button><button onClick={() => void copyCategoryId()}><Copy size={19} /><span>复制 ID</span></button></div></aside>
            <section className="catalog-rule-section"><h3>发布规则</h3><div className="catalog-rule-grid"><div><FileText size={16} /><span>标题上限</span><strong>{selected.maxTitleLength || "--"}</strong></div><div><Images size={16} /><span>图片上限</span><strong>{selected.maxPictures || "--"}</strong></div><div><Layers3 size={16} /><span>规格上限</span><strong>{selected.maxVariations || "--"}</strong></div><div><CircleDollarSign size={16} /><span>最低价格</span><strong>{selected.minimumPrice === null ? "--" : `${selected.minimumPrice} ${selected.minimumPriceCurrency ?? ""}`}</strong></div><div><Truck size={16} /><span>配送方式</span><strong>{selected.shippingOptions.length || "--"}</strong></div><div><PackageCheck size={16} /><span>库存必填</span><strong>{selected.stockRequired ? "是" : "否"}</strong></div></div></section>
            <section className="catalog-stat-section"><h3>类目统计概览</h3><div><span>直属下级<strong>{selected.childCount.toLocaleString()}</strong></span><span>属性数量<strong>{selected.attributeCount.toLocaleString()}</strong></span><span>必填属性<strong>{selected.requiredAttributeCount.toLocaleString()}</strong></span><span>规格属性<strong>{selected.variationAttributeCount.toLocaleString()}</strong></span><span>关联站点<strong>{relatedSites.length.toLocaleString()}</strong></span><span>商品数量<strong>{selected.totalItems.toLocaleString()}</strong></span></div></section>
          </div> : activeTab === "publish" ? <section className="catalog-tab-view publish-schema-view">
            <div className="catalog-view-heading"><div><h3>完整发布字段</h3><span>{publishSchema?.site.label ?? selected.siteCode} · {publishSchema?.site.currencyId ?? "--"}</span></div><div><span className="required">必填 {publishSchema?.summary.requiredFields ?? 0}</span><span className="conditional">条件 {publishSchema?.summary.conditionalFields ?? 0}</span><span>规格 {publishSchema?.summary.variationFields ?? 0}</span></div></div>
            {publishSchema?.warnings.length ? <div className="publish-schema-warning">{publishSchema.warnings.map((warning) => <span key={warning}>{warning}</span>)}</div> : null}
            {publishSchema ? <><div className="publish-schema-summary"><span>全部字段<strong>{publishSchema.summary.totalFields}</strong></span><span>通用字段<strong>{publishSchema.summary.commonFields}</strong></span><span>类目属性<strong>{publishSchema.summary.categoryAttributes}</strong></span><span>选填字段<strong>{publishSchema.summary.optionalFields}</strong></span></div><div className="table-scroll"><table className="data-table catalog-publish-table"><thead><tr><th>字段</th><th>来源</th><th>要求</th><th>填写层级</th><th>格式与可选值</th></tr></thead><tbody>{publishSchema.fields.map((field) => <tr key={`${field.source}:${field.fieldCode}`}><td><strong>{field.label}</strong><small className="mono">{field.fieldCode}</small></td><td>{field.source === "COMMON" ? "通用字段" : "类目属性"}</td><td><div className="attribute-flags"><span className={field.requirement === "REQUIRED" ? "required" : field.requirement === "CONDITIONAL" ? "conditional" : ""}>{requirementLabel(field.requirement)}</span>{field.catalogRequired ? <span>目录必填</span> : null}</div></td><td>{scopeLabel(field.scope)}</td><td><strong>{field.valueType || "--"}</strong>{field.valueCount ? <small title={field.valuePreview.map((value) => value.name).join("、")}>{field.valueCount.toLocaleString()} 个官方值</small> : constraintHint(field.constraints) ? <small>{constraintHint(field.constraints)}</small> : null}</td></tr>)}</tbody></table></div></> : <EmptyState title="发布规则尚未生成" />}
          </section> : activeTab === "attributes" ? <section className="catalog-tab-view"><div className="catalog-view-heading"><div><h3>官方属性</h3><span>{selected.attributeCount.toLocaleString()} 条</span></div><div><span className="required">必填 {selected.requiredAttributeCount}</span><span>规格 {selected.variationAttributeCount}</span></div></div>{attributes.length ? <div className="table-scroll"><table className="data-table catalog-attribute-table"><thead><tr><th>属性</th><th>类型</th><th>规则</th><th>可选值</th></tr></thead><tbody>{attributes.map((attribute) => <tr key={attribute.attributeId}><td><strong>{attribute.name}</strong><small className="mono">{attribute.attributeId}</small></td><td>{attribute.valueType || "--"}</td><td><div className="attribute-flags">{attribute.required ? <span className="required">必填</span> : null}{attribute.variation ? <span>规格</span> : null}{!attribute.required && !attribute.variation ? <span>可选</span> : null}</div></td><td><strong>{attribute.valueCount.toLocaleString()}</strong>{attribute.valueCount ? <small title={valuePreview(attribute.values)}>{valuePreview(attribute.values) || "官方可选值"}</small> : null}</td></tr>)}</tbody></table></div> : <EmptyState title="该类目没有官方属性" />}</section> : activeTab === "sites" ? <section className="catalog-tab-view"><div className="catalog-view-heading"><div><h3>关联站点</h3><span>按官方目录域匹配</span></div></div>{relatedSites.length ? <div className="catalog-site-list">{relatedSites.map((site) => <div key={`${site.siteCode}:${site.categoryId}`}><span className="catalog-site-code">{site.siteCode}</span><span><strong>{site.siteLabel}</strong><small>{site.name} · {site.categoryId}</small></span>{site.listingAllowed ? <BadgeCheck size={16} /> : <X size={16} />}</div>)}</div> : <EmptyState title="没有找到其他站点的同域类目" />}</section> : <section className="catalog-tab-view"><div className="catalog-view-heading"><div><h3>完整类目路径</h3><span>共 {selected.path.length} 级</span></div></div><ol className="catalog-path-list">{selected.path.map((part, index) => <li key={part.id} className={part.id === selected.categoryId ? "current" : ""}><span>{index + 1}</span><div><strong>{part.name}</strong><code>{part.id}</code></div>{part.id === selected.categoryId ? <BadgeCheck size={17} /> : null}</li>)}</ol></section>}
        </> : <div className="catalog-detail-empty"><FolderOpen size={30} /><strong>选择一个类目</strong><span>类目信息会显示在这里</span></div>}
      </main>
    </div>
  </div>;
}
